import { apiFetch, isApiMode } from './http';

// Client for the platform staff directory and role assignment.
//
// Everything here is a VIEW of what the server decided. The UI mirrors the
// server's read/write split (support may look, only superadmin may change) so
// the page does not offer a control the API will refuse — but the refusal is
// the API's, always. Hiding a button is a courtesy, not a control.

export const PLATFORM_ROLES = [
  'PLATFORM_SUPERADMIN',
  'PLATFORM_SUPPORT',
  'PLATFORM_FINANCE',
  'PLATFORM_OPERATIONS',
  'PLATFORM_READONLY',
] as const;
export type PlatformRole = (typeof PLATFORM_ROLES)[number];

export interface PlatformUser {
  id: string;
  status: string;
  displayName: string | null;
  email: string | null;
  identities: { provider: string; subject: string; lastLoginAt: string | null }[];
  platformRoles: PlatformRole[];
  tenantMembershipCount: number;
  /** StarPoint's own person (Entra identity or a platform role), not a customer. */
  starpoint: boolean;
}

/**
 * WHICH list came back.
 *
 * `starpoint` is the default directory — StarPoint's own people. `search`
 * means an explicit query, which deliberately reaches customer accounts too,
 * because «why can't this person see X?» is usually asked about a customer.
 */
export type DirectoryScope = 'starpoint' | 'search';

export type DirectoryResult =
  | { state: 'no-api' }
  | { state: 'ok'; users: PlatformUser[]; truncated: boolean; scope: DirectoryScope }
  | { state: 'forbidden' }
  | { state: 'error'; status: number | null };

export async function fetchPlatformUsers(q = ''): Promise<DirectoryResult> {
  if (!isApiMode()) return { state: 'no-api' };
  try {
    const path = q ? `/platform/users?q=${encodeURIComponent(q)}` : '/platform/users';
    const res = await apiFetch(path);
    if (res.status === 401 || res.status === 403) return { state: 'forbidden' };
    if (!res.ok) return { state: 'error', status: res.status };
    const body = (await res.json()) as {
      users: PlatformUser[];
      truncated: boolean;
      scope?: DirectoryScope;
    };
    return {
      state: 'ok',
      users: body.users,
      truncated: body.truncated,
      // An older API that does not say is reporting whatever the query asked
      // for; deriving it here keeps the page honest rather than guessing.
      scope: body.scope ?? (q ? 'search' : 'starpoint'),
    };
  } catch {
    return { state: 'error', status: null };
  }
}

export type RoleChangeResult =
  | { state: 'ok'; platformRoles: PlatformRole[] }
  | { state: 'forbidden' }
  /** The server refused because it would remove the last superadmin. */
  | { state: 'last-superadmin'; message: string }
  /**
   * The account has no Microsoft (Entra) identity, so a platform role could
   * never be exercised and the server refuses to grant one (#1030). The CODE
   * is the server's; the sentence is the page's.
   */
  | { state: 'no-entra-identity' }
  | { state: 'error'; status: number | null };

async function roleChange(path: string, init: RequestInit): Promise<RoleChangeResult> {
  if (!isApiMode()) return { state: 'error', status: null };
  try {
    const res = await apiFetch(path, init);
    if (res.status === 401 || res.status === 403) return { state: 'forbidden' };
    if (res.status === 409) {
      const body = (await res.json().catch(() => null)) as {
        error?: string;
        message?: string;
      } | null;
      // TWO 409s, told apart by CODE: a grant to an account the platform would
      // never honour (#1030), and the last-superadmin guard on a revoke.
      if (body?.error === 'no_entra_identity') return { state: 'no-entra-identity' };
      return {
        state: 'last-superadmin',
        message: body?.message ?? 'Kan ikke fjerne siste superadmin.',
      };
    }
    if (!res.ok) return { state: 'error', status: res.status };
    const body = (await res.json()) as { platformRoles: PlatformRole[] };
    return { state: 'ok', platformRoles: body.platformRoles };
  } catch {
    return { state: 'error', status: null };
  }
}

export function grantPlatformRole(userId: string, role: PlatformRole): Promise<RoleChangeResult> {
  return roleChange(`/platform/users/${encodeURIComponent(userId)}/roles`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ role }),
  });
}

export function revokePlatformRole(userId: string, role: PlatformRole): Promise<RoleChangeResult> {
  return roleChange(
    `/platform/users/${encodeURIComponent(userId)}/roles/${encodeURIComponent(role)}`,
    { method: 'DELETE' },
  );
}

// ─────────────────────────────────────────────────────────────────────
// ACCOUNT LIFECYCLE (#398): shutting an account off, and switching it back on.
//
// It used to mean «a developer runs SQL». It is refused again server-side —
// the union below is the SERVER's answer, rendered, not a condition this
// module decides.
//
// THERE IS NO «SEND A PASSWORD LINK» CALL. Its route left with the password
// (#754), and the button that kept calling it left in #1003.
// ─────────────────────────────────────────────────────────────────────

export type AccountStatus = 'active' | 'disabled';
/** What the directory can report — `closed` (#1062) is reached only through `closeAccount`. */
export type ListedAccountStatus = AccountStatus | 'closed';

export type StatusChangeResult =
  | { state: 'ok'; status: AccountStatus; sessionsRevoked: number; changed: boolean }
  /** Disabling would leave nobody able to manage platform roles. */
  | { state: 'last-role-manager'; message: string }
  /** You cannot disable the account you are signed in as. */
  | { state: 'cannot-disable-self'; message: string }
  | { state: 'forbidden' }
  | { state: 'error'; status: number | null };

export async function setAccountStatus(
  userId: string,
  status: AccountStatus,
): Promise<StatusChangeResult> {
  if (!isApiMode()) return { state: 'error', status: null };
  try {
    const res = await apiFetch(`/platform/users/${encodeURIComponent(userId)}/status`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ status }),
    });
    if (res.status === 401 || res.status === 403) return { state: 'forbidden' };
    if (res.status === 409) {
      const body = (await res.json().catch(() => null)) as {
        error?: string;
        message?: string;
      } | null;
      // The server's own sentence, shown verbatim — each explains the way out,
      // and paraphrasing them here would let the two drift.
      const message = body?.message ?? 'Serveren avslo endringen.';
      return body?.error === 'cannot_disable_self'
        ? { state: 'cannot-disable-self', message }
        : { state: 'last-role-manager', message };
    }
    if (!res.ok) return { state: 'error', status: res.status };
    const body = (await res.json()) as {
      status: AccountStatus;
      sessionsRevoked: number;
      changed: boolean;
    };
    return {
      state: 'ok',
      status: body.status,
      sessionsRevoked: body.sessionsRevoked,
      changed: body.changed,
    };
  } catch {
    return { state: 'error', status: null };
  }
}

// ─────────────────────────────────────────────────────────────────────
// CLOSING AN ACCOUNT ON THE PERSON'S BEHALF (#1062).
//
// `POST /platform/users/:id/close { reason, supportReference }`. Account
// closure, not containment: the account stops signing in for good (nothing
// here reopens it), and nothing else about it changes. The reason is a CODE
// and the reference a ticket id the server holds to a shape that cannot carry
// a name or an address.
// ─────────────────────────────────────────────────────────────────────

export const CLOSURE_REASONS = ['owner_request', 'compromised', 'deceased'] as const;
export type ClosureReason = (typeof CLOSURE_REASONS)[number];

/** The server's own rule for a reference, so the form can say so before sending. */
export const SUPPORT_REFERENCE = /^[A-Za-z0-9._:#/-]{1,64}$/;

export type CloseAccountResult =
  | { state: 'ok'; changed: boolean; sessionsRevoked: number }
  /** The reason or the reference was refused. */
  | { state: 'invalid' }
  /** Closing would leave nobody able to manage platform roles. */
  | { state: 'last-role-manager'; message: string }
  /** Not your own account, not under an impersonation context, or no right. */
  | { state: 'forbidden' }
  | { state: 'error'; status: number | null };

export async function closeAccount(
  userId: string,
  reason: ClosureReason,
  supportReference: string,
): Promise<CloseAccountResult> {
  if (!isApiMode()) return { state: 'error', status: null };
  try {
    const res = await apiFetch(`/platform/users/${encodeURIComponent(userId)}/close`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ reason, supportReference }),
    });
    if (res.status === 400) return { state: 'invalid' };
    if (res.status === 401 || res.status === 403) return { state: 'forbidden' };
    if (res.status === 409) {
      const body = (await res.json().catch(() => null)) as { message?: string } | null;
      return { state: 'last-role-manager', message: body?.message ?? '' };
    }
    if (!res.ok) return { state: 'error', status: res.status };
    const body = (await res.json()) as { changed?: boolean; sessionsRevoked?: number };
    return {
      state: 'ok',
      changed: body.changed === true,
      sessionsRevoked: typeof body.sessionsRevoked === 'number' ? body.sessionsRevoked : 0,
    };
  } catch {
    return { state: 'error', status: null };
  }
}
